import { useV2NewEntry } from "src/generatorV2/hooks/useV2NewEntry";
import "../App.scss";

import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { GeneratorV2 } from "src/generatorV2";
import { useV2AutoSave } from "src/generatorV2/hooks/useV2AutoSave";
import { useV2EntryLock } from "src/generatorV2/hooks/useV2EntryLock";
import { useGetApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { initEntryLockData, resetEntryLock } from "src/redux/slices/entrySlice";
import {
  clearPrevEntryData,
  resetState,
  setIsInitialInsert,
  setLogbookData,
  setLogbookDataMinimalV2,
} from "src/redux/slices/generatorSlice";
import { Flex, PageContainer } from "src/styled";

import useResetGuernsey from "../components/shared/GuernseyMulti/useResetGuernsey";
import { useAutoSave } from "./hooks/useAutoSave";
import { useEntryLock } from "./hooks/useEntryLock";
import { useFetchSnapshot } from "./hooks/useFetchSnapshot";
import { useGetBridgeUsers } from "./hooks/useGetBridgeUsers";
import useNewEntryRequests from "./hooks/useNewEntryRequests";
import LogBar from "./LogBar/LogBar";
import MedStatusSelect from "./LogBar/MedStatusSelect";
import Logbook from "./Logbook/Logbook";
import Modals from "./Modals";

const GeneratorV1Hooks = () => {
  useAutoSave();
  useEntryLock();
  useGetBridgeUsers();
  useNewEntryRequests();
  return null;
};

const GeneratorV2Hooks = () => {
  useV2EntryLock();
  useGetBridgeUsers();
  useNewEntryRequests();
  useV2AutoSave({ enableAutosave: true });
  useV2NewEntry();
  return null;
};

const Generator = () => {
  const dispatch = useDispatch();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const resetGuernsey = useResetGuernsey();
  const pageRef = useRef<HTMLDivElement | null>(null);
  const { data, refetch } = useGetApi<any>({
    request: "get-logbook",
    body: {
      _part: String(idType) === "partid",
      params: {
        lbid: logbookId,
        [String(idType)]: lbeidOrPartId,
      },
    },
  });

  // Determine if this is a V2 entry early (before data effect)
  const { isV2, lbEntry, entryInfo } = useMemo(() => {
    const latest = data?.data?.content?.data?.latest;
    if (!latest) {
      return { isV2: false, lbEntry: null, entryInfo: null };
    }
    const lbEntry = latest?.lbentry;
    const { lbentry, ...entryInfo } = latest;
    return { isV2: lbEntry?.generator === "v2", lbEntry, entryInfo };
  }, [data]);

  useEffect(() => {
    if (data) {
      resetGuernsey();
      // For V2, set minimal Redux state to avoid unnecessary V1 processing
      if (isV2) {
        dispatch(setLogbookDataMinimalV2(data));
      } else {
        dispatch(setLogbookData(data));
      }
      dispatch(setIsInitialInsert(true));
      dispatch(
        initEntryLockData({
          ...data.data.content.data.lock,
          current: data?.data?.content?.data?.latest?.updated,
          editing: false,
          activeAutosave: false,
        }),
      );
    }
  }, [data, dispatch, resetGuernsey, isV2]);

  const { pageSize, setPageSize } = useFetchSnapshot();

  useEffect(() => {
    setSearchParams(
      prev => {
        prev.delete("new");
        return prev;
      },
      { replace: true },
    );
    return () => {
      dispatch(resetState());
      dispatch(resetEntryLock());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const memoizedRef = useMemo(() => pageRef, [pageRef]);

  return (
    <PageContainer id="page-container" ref={pageRef}>
      {/* {!(window as any).Cypress && ( */}
      {/*   <ExpiredShipsTimeModal key={logbookDataSet} /> */}
      {/* )} */}
      {isV2 ? <GeneratorV2Hooks /> : <GeneratorV1Hooks />}
      <LogBar />
      <Flex justify="end">
        <MedStatusSelect />
      </Flex>
      {isV2 ? (
        <GeneratorV2 logbookDef={lbEntry} entryInfo={entryInfo} />
      ) : (
        <Logbook
          pageRef={memoizedRef}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          htmlId="lbentry"
        />
      )}
      <Modals refetchLogbook={refetch || (() => {})} />
    </PageContainer>
  );
};

const GeneratorFormProvider = () => {
  // const { locked24 } = useSelector(state => state.generatorSlice);
  const dispatch = useDispatch();
  const methods = useForm();
  const refetchIterator = useSelector(
    state => state.generatorSlice.refetchIterator,
  );
  const { lbeidOrPartId } = useParams();
  const [searchParams] = useSearchParams();
  const isNewEntry = searchParams.get("new") === "true";

  // Reset form when entry changes or when switching to/from new entry
  useEffect(() => {
    methods.reset();
    dispatch(clearPrevEntryData());
  }, [lbeidOrPartId, isNewEntry, methods, dispatch]);

  return (
    <FormProvider {...methods}>
      <Generator key={refetchIterator} />
    </FormProvider>
  );
};

export default GeneratorFormProvider;
